import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Plane, PlaneTakeoff, PlaneLanding, MapPin, Clock, Ticket,
  Globe, Languages, Euro, ThermometerSun, BedDouble,
  ArrowLeft, ArrowRight, ExternalLink, Users, User, Wallet, Smartphone, Receipt,
} from "lucide-react";
import FsLogo from "@/components/FsLogo";

/* ============ Motion ============ */
const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

/* ============ Mock data — DECLASSIFICATO ============ */
const FACTS = [
  { Icon: Globe, k: "PAESE", v: "Spagna" },
  { Icon: MapPin, k: "REGIONE", v: "Catalogna" },
  { Icon: Languages, k: "LINGUA", v: "Català / Español" },
  { Icon: Euro, k: "VALUTA", v: "Euro (€)" },
  { Icon: Clock, k: "FUSO", v: "GMT+1 · come a casa" },
  { Icon: ThermometerSun, k: "METEO", v: "≈ 15°C · sole" },
];

const FLIGHTS = [
  {
    label: "ANDATA",
    Icon: PlaneTakeoff,
    date: "Venerdì 5 Febbraio 2027",
    airline: "Wizz Air",
    number: "W4 6315",
    duration: "1h 40m",
    from: { code: "MXP", city: "Milano Malpensa", time: "10:45" },
    to: { code: "BCN", city: "Barcellona · El Prat", time: "12:25", hot: true },
    meta: [
      { Icon: Plane, k: "COMPAGNIA", v: "Wizz Air" },
      { Icon: Ticket, k: "VOLO", v: "W4 6315" },
    ],
  },
  {
    label: "RITORNO",
    Icon: PlaneLanding,
    date: "Domenica 7 Febbraio 2027",
    airline: "easyJet",
    number: "U2 3750",
    duration: "1h 40m",
    from: { code: "BCN", city: "Barcellona · El Prat", time: "22:05", hot: true },
    to: { code: "MXP", city: "Milano Malpensa", time: "23:45" },
    meta: [
      { Icon: Plane, k: "COMPAGNIA", v: "easyJet" },
      { Icon: Ticket, k: "VOLO", v: "U2 3750" },
    ],
  },
];

const HOTEL = {
  name: "Stop Parc Güell",
  city: "Barcellona · Spagna",
  url: "https://www.booking.com/hotel/es/bcn-stop-parc-guell.it.html",
  checkin: "Venerdì 5 Febbraio 2027",
  checkout: "Domenica 7 Febbraio 2027",
};

const FRIDEY_URL =
  "https://fridey-travel.com/tripDetails?tripId=45ffb1d2-021e-4ac5-9859-4df583d750ea&activityId=d59781f1-6619-4007-b416-2ba366e565f0";

const APARTMENTS = [
  {
    n: "01",
    capacity: "9 PERSONE",
    note: null,
    rooms: [
      { Icon: Users, type: "Camera tripla", guests: ["Fabio", "Domenico", "Nico"] },
      { Icon: Users, type: "Doppia", guests: ["Federica Toscano", "Matteo Pavanello"] },
      { Icon: Users, type: "Doppia", guests: ["Isabella", "Gianluca"] },
      { Icon: Users, type: "Doppia", guests: ["Clara", "Serena"] },
    ],
  },
  {
    n: "02",
    capacity: "8 PERSONE",
    note: "di cui 1 divano letto",
    rooms: [
      { Icon: Users, type: "Doppia", guests: ["Giorgio", "Martina"] },
      { Icon: Users, type: "Doppia", guests: ["Lisa", "Giulia"] },
      { Icon: Users, type: "Doppia", guests: ["Alberto", "Riccardo"] },
      { Icon: User, type: "Singola", guests: ["Federica Seren"] },
      { Icon: User, type: "Singola", guests: ["Peppino"] },
    ],
  },
];

const ITINERARY = [
  {
    n: "01",
    date: "Venerdì 5 Febbraio 2027",
    title: "ANDATA",
    note: "Si parte. Documento alla mano.",
    events: [
      { time: "10:45", Icon: PlaneTakeoff, t: "Partenza da Milano Malpensa", b: "Volo W4 6315, Wizz Air." },
      { time: "12:25", Icon: PlaneLanding, t: "Arrivo a Barcellona · El Prat", b: "Si tocca terra in Catalogna." },
      { time: "—", Icon: BedDouble, t: `Check-in · ${HOTEL.name}`, b: "Molla la valigia. Il resto si decide sul posto." },
    ],
  },
  {
    n: "02",
    date: "Domenica 7 Febbraio 2027",
    title: "RITORNO",
    note: "Fine missione.",
    events: [
      { time: "22:05", Icon: PlaneTakeoff, t: "Partenza da Barcellona · El Prat", b: "Volo U2 3750, easyJet." },
      { time: "23:45", Icon: PlaneLanding, t: "Arrivo a Milano Malpensa", b: "Si rientra. Già nostalgia." },
    ],
  },
];

/* ===================================================== */
/* PAGE                                                  */
/* ===================================================== */
export default function DestinationPage() {
  return (
    <div className="bg-ink text-bone min-h-screen">
      <Reveal />
      <Fridey />
      <Flights />
      <Hotel />
      <Program />
      <Footer />
    </div>
  );
}

/* ===================================================== */
/* REVEAL (hero)                                         */
/* ===================================================== */
function Reveal() {
  const reduce = useReducedMotion();
  const navigate = useNavigate();

  return (
    <section
      data-testid="destination-hero"
      className="relative min-h-[100dvh] flex flex-col overflow-hidden grid-bg spotlight"
    >
      {/* Top bar */}
      <header className="relative z-10 w-full max-w-[1280px] mx-auto px-6 sm:px-10 pt-7 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 font-mono text-bone-dim hover:text-red transition-colors tracking-[0.18em] text-[11px] sm:text-xs"
        >
          <ArrowLeft size={14} strokeWidth={2} /> FASCICOLO
        </button>
        <span className="font-mono text-bone-dim text-[11px] sm:text-xs tracking-[0.24em]">
          FASCICOLO N&deg;30 / DECLASSIFICATO
        </span>
      </header>

      {/* Center */}
      <div className="relative z-10 flex-1 flex items-center px-6 sm:px-10 py-16">
        <div className="w-full max-w-[1280px] mx-auto">
          {/* Declassified stamp */}
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 1.4, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: -4 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mb-10"
          >
            <span className="stamp text-xl sm:text-2xl">DECLASSIFICATO</span>
          </motion.div>

          <motion.span
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="kicker mb-6"
          >
            DESTINAZIONE CONFERMATA
          </motion.span>

          <motion.h1
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="font-display leading-[0.86] text-bone"
            style={{ fontSize: "clamp(2.8rem, 12vw, 7.5rem)", letterSpacing: "0.005em" }}
          >
            BARCELLONA<span className="text-red">.</span>
          </motion.h1>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.42, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-bone-dim text-sm tracking-wider"
          >
            <span className="inline-flex items-center gap-2">
              <MapPin size={14} className="text-red" strokeWidth={1.8} /> SPAGNA · CATALOGNA
            </span>
            <span className="hidden sm:inline text-bone-faint">/</span>
            <span>41&deg;23&prime; N · 2&deg;10&prime; E</span>
          </motion.div>

          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.56 }}
            className="mt-7 text-bone-dim text-base sm:text-lg leading-relaxed max-w-xl"
          >
            Tre giorni tra Gaudí, tapas e mare di febbraio. Volo e hotel coperti:
            tu pensa solo a presentarti in aeroporto con il documento giusto.
          </motion.p>
          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.66 }}
            className="mt-3 font-mono italic text-red text-sm sm:text-base"
          >
            &ldquo;Ve l&apos;avevo detto: al resto pensavo io.&rdquo;
          </motion.p>

          {/* Facts strip */}
          <motion.div
            variants={fadeUp}
            custom={1}
            initial={reduce ? false : "hidden"}
            animate="visible"
            className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-line border border-line max-w-4xl"
          >
            {FACTS.map((f) => (
              <div key={f.k} className="bg-ink-2 px-4 py-5 flex flex-col gap-2">
                <f.Icon size={16} className="text-red" strokeWidth={1.6} />
                <span className="field-label">{f.k}</span>
                <span className="font-mono text-bone text-sm tracking-wide">{f.v}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Foot strip */}
      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 sm:px-10 pb-7">
        <div className="rule-dotted mb-4" />
        <div className="flex items-center justify-between font-mono text-[10px] sm:text-[11px] tracking-[0.24em] text-bone-faint">
          <span>FINESTRA: 05-07 FEB 2027</span>
          <span className="hidden sm:inline">STATO: CONFERMATO</span>
        </div>
      </div>
    </section>
  );
}

/* ===================================================== */
/* FLIGHTS (piano di volo)                               */
/* ===================================================== */
function Flights() {
  const reduce = useReducedMotion();

  return (
    <section
      data-testid="flights-section"
      className="relative px-6 sm:px-10 py-24 sm:py-32 border-t border-line"
    >
      <div className="max-w-[1280px] mx-auto">
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-14"
        >
          <div>
            <span className="kicker mb-5">DOCUMENTI DI VIAGGIO</span>
            <h2 className="font-display text-bone leading-none" style={{ fontSize: "clamp(2.2rem, 6vw, 4rem)" }}>
              IL PIANO DI VOLO
            </h2>
          </div>
          <span className="font-mono text-bone-dim text-sm tracking-[0.18em]">WIZZ AIR · EASYJET · DIRETTI</span>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {FLIGHTS.map((f, i) => (
            <FlightCard key={f.label} f={f} i={i} reduce={reduce} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FlightCard({ f, i, reduce }) {
  return (
    <motion.div
      variants={fadeUp}
      custom={i}
      initial={reduce ? false : "hidden"}
      whileInView="visible"
      viewport={{ once: true, amount: 0.25 }}
      data-testid={`flight-${f.label.toLowerCase()}`}
      className="bg-ink-2 border border-line p-7 sm:p-9"
    >
      {/* Card header */}
      <div className="flex items-center justify-between mb-7">
        <span className="inline-flex items-center gap-2.5">
          <f.Icon size={18} className="text-red" strokeWidth={1.7} />
          <span className="field-label text-bone">{f.label}</span>
        </span>
        <span className="font-mono text-bone-faint text-xs tracking-wider">{f.date}</span>
      </div>

      {/* Route */}
      <div className="flex items-stretch gap-3 sm:gap-5">
        <div className="text-left shrink-0">
          <div className="font-display text-bone leading-none" style={{ fontSize: "clamp(2.4rem, 7vw, 3.4rem)" }}>
            {f.from.code}
          </div>
          <div className={`font-mono text-lg mt-2 tracking-wider ${f.from.hot ? "text-red" : "text-bone"}`}>
            {f.from.time}
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-1 pt-2">
          <Plane size={18} className="text-bone-dim mb-2 rotate-90" strokeWidth={1.6} />
          <div className="w-full rule-dotted" />
          <span className="field-label mt-2 whitespace-nowrap">{f.duration}</span>
        </div>

        <div className="text-right shrink-0">
          <div className="font-display text-bone leading-none" style={{ fontSize: "clamp(2.4rem, 7vw, 3.4rem)" }}>
            {f.to.code}
          </div>
          <div className={`font-mono text-lg mt-2 tracking-wider ${f.to.hot ? "text-red" : "text-bone"}`}>
            {f.to.time}
          </div>
        </div>
      </div>

      {/* City names */}
      <div className="flex items-center justify-between mt-3 font-mono text-bone-faint text-[11px] tracking-wide">
        <span>{f.from.city}</span>
        <span className="text-right">{f.to.city}</span>
      </div>

      {/* Meta grid */}
      <div className="grid grid-cols-2 gap-px bg-line border border-line mt-8">
        {f.meta.map((m) => (
          <div key={m.k} className="bg-ink-2 px-3 py-4 flex flex-col gap-1.5">
            <span className="inline-flex items-center gap-1.5 field-label">
              <m.Icon size={12} className="text-bone-faint" strokeWidth={1.7} />
              {m.k}
            </span>
            <span className="font-mono text-bone text-sm tracking-wide">{m.v}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ===================================================== */
/* HOTEL                                                 */
/* ===================================================== */
function Hotel() {
  const reduce = useReducedMotion();

  return (
    <section
      data-testid="hotel-section"
      className="relative px-6 sm:px-10 py-24 sm:py-32 border-t border-line"
    >
      <div className="max-w-[1280px] mx-auto">
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-14"
        >
          <div>
            <span className="kicker mb-5">BASE OPERATIVA</span>
            <h2 className="font-display text-bone leading-none" style={{ fontSize: "clamp(2.2rem, 6vw, 4rem)" }}>
              L&apos;ALLOGGIO
            </h2>
          </div>
          <span className="font-mono text-bone-dim text-sm tracking-[0.18em]">2 APPARTAMENTI · 17 PERSONE</span>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          className="bg-ink-2 border border-line p-7 sm:p-9"
        >
          <div className="flex items-center justify-between mb-7">
            <span className="inline-flex items-center gap-2.5">
              <BedDouble size={18} className="text-red" strokeWidth={1.7} />
              <span className="field-label text-bone">ALLOGGIO</span>
            </span>
            <span className="font-mono text-bone-faint text-xs tracking-wider">{HOTEL.city}</span>
          </div>

          <h3
            className="font-display text-bone leading-none"
            style={{ fontSize: "clamp(2rem, 5.5vw, 3.2rem)" }}
          >
            {HOTEL.name.toUpperCase()}
          </h3>

          <div className="grid grid-cols-2 gap-px bg-line border border-line mt-8">
            <div className="bg-ink-2 px-3 py-4 flex flex-col gap-1.5">
              <span className="inline-flex items-center gap-1.5 field-label">
                <Clock size={12} className="text-bone-faint" strokeWidth={1.7} />
                CHECK-IN
              </span>
              <span className="font-mono text-bone text-sm tracking-wide">{HOTEL.checkin}</span>
            </div>
            <div className="bg-ink-2 px-3 py-4 flex flex-col gap-1.5">
              <span className="inline-flex items-center gap-1.5 field-label">
                <Clock size={12} className="text-bone-faint" strokeWidth={1.7} />
                CHECK-OUT
              </span>
              <span className="font-mono text-bone text-sm tracking-wide">{HOTEL.checkout}</span>
            </div>
          </div>
        </motion.div>

        {/* Ripartizione appartamenti */}
        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          {APARTMENTS.map((apt, i) => (
            <motion.div
              key={apt.n}
              variants={fadeUp}
              custom={i}
              initial={reduce ? false : "hidden"}
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              data-testid={`apartment-${apt.n}`}
              className="bg-ink-2 border border-line p-7 sm:p-9"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="inline-flex items-baseline gap-4">
                  <span className="font-mono text-red text-sm tracking-widest">{apt.n}</span>
                  <span
                    className="font-display text-bone leading-none"
                    style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)" }}
                  >
                    APPARTAMENTO
                  </span>
                </span>
                <span className="field-label text-bone whitespace-nowrap">{apt.capacity}</span>
              </div>

              {apt.note && (
                <p className="font-mono italic text-bone-faint text-sm mb-6">{apt.note}</p>
              )}

              <div className="border-t border-line">
                {apt.rooms.map((r, ri) => (
                  <div
                    key={ri}
                    className={`flex gap-4 sm:gap-5 py-4 ${ri !== 0 ? "border-t border-line" : ""}`}
                  >
                    <div className="shrink-0 pt-1">
                      <r.Icon size={16} className="text-bone-faint" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="field-label">{r.type}</span>
                      <p className="font-mono text-bone text-sm tracking-wide mt-1.5 leading-relaxed">
                        {r.guests.join(" · ")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================================================== */
/* PROGRAM (itinerario)                                  */
/* ===================================================== */
function Program() {
  const reduce = useReducedMotion();

  return (
    <section
      data-testid="program-section"
      className="relative px-6 sm:px-10 py-24 sm:py-36 border-t border-line"
    >
      <div className="max-w-[1280px] mx-auto">
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="mb-16"
        >
          <span className="kicker mb-5">ORDINE DI MISSIONE</span>
          <h2 className="font-display text-bone leading-none" style={{ fontSize: "clamp(2.4rem, 6vw, 4.5rem)" }}>
            IL PROGRAMMA
          </h2>
          <p className="mt-6 text-bone-dim text-lg leading-relaxed max-w-xl">
            L'essenziale: come si parte, come si arriva, dove si dorme.
            Il resto si decide sul posto.
          </p>
        </motion.div>

        <div className="space-y-16">
          {ITINERARY.map((day, di) => (
            <motion.div
              key={day.n}
              variants={fadeUp}
              custom={di}
              initial={reduce ? false : "hidden"}
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              className="grid lg:grid-cols-12 gap-8 lg:gap-16"
            >
              {/* Day header */}
              <div className="lg:col-span-4 lg:sticky lg:top-12 self-start">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-red text-sm tracking-widest">{day.n}</span>
                  <h3 className="font-display text-bone leading-none" style={{ fontSize: "clamp(1.9rem, 4vw, 2.8rem)" }}>
                    {day.title}
                  </h3>
                </div>
                <p className="font-mono text-bone-dim text-sm tracking-wider mt-4">{day.date}</p>
                <p className="font-mono italic text-bone-faint text-sm mt-3 max-w-xs leading-relaxed">
                  {day.note}
                </p>
              </div>

              {/* Timeline */}
              <div className="lg:col-span-8">
                {day.events.map((e, ei) => (
                  <div
                    key={ei}
                    className={`flex gap-5 sm:gap-7 py-6 ${ei !== 0 ? "border-t border-line" : ""}`}
                  >
                    <div className="shrink-0 w-14 sm:w-16 pt-1">
                      <span className="font-mono text-red text-sm tracking-wider tabular-nums">{e.time}</span>
                    </div>
                    <div className="shrink-0 pt-0.5">
                      <e.Icon size={20} className="text-bone" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-grotesk text-bone text-lg sm:text-xl leading-snug">{e.t}</h4>
                      <p className="text-bone-dim leading-relaxed mt-1.5 max-w-lg">{e.b}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===================================================== */
/* FRIDEY                                                */
/* ===================================================== */
function Fridey() {
  const reduce = useReducedMotion();

  const points = [
    { Icon: Receipt, t: "Le spese si dividono lì", b: "Chi paga cosa, quanto deve rientrare a chi. Niente conti sul tovagliolo." },
    { Icon: Wallet, t: "Saldi sempre aggiornati", b: "In ogni momento sai a che punto sei. Si pareggia tutto alla fine." },
    { Icon: Smartphone, t: "Serve a tutti e diciassette", b: "Scaricatela prima di partire: sul posto è tardi e c'è chi non ha campo." },
  ];

  return (
    <section
      data-testid="fridey-section"
      className="relative px-6 sm:px-10 py-24 sm:py-32 border-t border-line"
    >
      <div className="max-w-[1280px] mx-auto">
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-14"
        >
          <div>
            <span className="kicker mb-5">CENTRALE OPERATIVA</span>
            <h2 className="font-display text-bone leading-none" style={{ fontSize: "clamp(2.2rem, 6vw, 4rem)" }}>
              TUTTO SU FRIDEY
            </h2>
          </div>
          <span className="font-mono text-bone-dim text-sm tracking-[0.18em]">OBBLIGATORIA · PER TUTTI</span>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          className="bg-ink-2 border border-line p-7 sm:p-9"
        >
          <p className="text-bone-dim text-lg leading-relaxed max-w-2xl">
            Il viaggio è già caricato su Fridey. Da lì si gestisce tutto: programma,
            e soprattutto <span className="text-bone">le spese da dividere</span> mentre siamo lì.
          </p>
          <p className="font-mono italic text-bone-faint text-sm mt-4">
            “Scaricatela adesso, non quando siamo al bancone a fare i conti.”
          </p>

          <div className="grid sm:grid-cols-3 gap-px bg-line border border-line mt-9">
            {points.map((p) => (
              <div key={p.t} className="bg-ink-2 px-5 py-6 flex flex-col gap-3">
                <p.Icon size={20} className="text-red" strokeWidth={1.6} />
                <h4 className="font-grotesk text-bone text-base leading-snug">{p.t}</h4>
                <p className="text-bone-dim text-sm leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>

          <a
            href={FRIDEY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fridey-link"
            className="btn btn-primary px-8 py-4 mt-9 inline-flex items-center gap-2.5"
          >
            APRI IL VIAGGIO SU FRIDEY
            <ExternalLink size={16} strokeWidth={1.8} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ===================================================== */
/* FOOTER                                                */
/* ===================================================== */
function Footer() {
  const navigate = useNavigate();
  return (
    <footer data-testid="destination-footer" className="relative px-6 sm:px-10 pt-16 pb-10 border-t border-line">
      <div className="max-w-[1280px] mx-auto flex flex-col items-center text-center gap-6">
        <FsLogo size={48} />
        <div className="rule-dotted max-w-xs" />
        <div>
          <p className="font-mono text-bone tracking-[0.24em] text-sm">
            OPERAZIONE TRENTA / BARCELLONA / 05-07 FEB 2027
          </p>
          <p className="font-mono italic text-bone-faint text-sm mt-3 max-w-md">
            Preparate il fegato. Al resto, come sempre, penso io.
          </p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="btn btn-ghost px-7 py-3.5 mt-1"
        >
          <ArrowLeft size={15} strokeWidth={2} />
          Torna al fascicolo
        </button>
      </div>
    </footer>
  );
}
